import type { GlReportLine } from '@/services/accounting-service';

/**
 * Pure helpers for the GL detail drill-down modal (gl-detail-modal.tsx).
 * Kept free of React so the sort logic can be unit-tested without rendering the
 * modal (which pulls FlexModal + react-query). Mirrors the report-columns split.
 */

export const SOURCE_LABEL: Record<string, string> = {
	charge: 'Charge',
	payment: 'Payment',
	bill: 'Bill',
	bill_payment: 'Bill payment',
	credit: 'Credit',
	refund: 'Refund',
	bank_transfer: 'Transfer',
	import: 'Imported',
	manual: 'Manual',
	journal_entry: 'Journal',
};

export const sourceLabel = (k: string): string => SOURCE_LABEL[k] ?? k;

// QB-import sourceRef is stored as `<associationId>:<transNum>` for global
// uniqueness. The Num column shows just the trans number.
export const shortRef = (ref: string | null): string => {
	if (!ref) return '';
	const i = ref.lastIndexOf(':');
	return i >= 0 ? ref.slice(i + 1) : ref;
};

// Numeric view of the short ref for sorting — matches the Journal report's
// `Number(shortRef(...))` so `:10` sorts after `:2`. Non-numeric refs → 0.
export const shortRefNum = (ref: string | null): number => Number(shortRef(ref)) || 0;

/**
 * What this modal's "Num" column shows: the entry_number sequence when there is
 * one, else the source document number (bill #, check #, invoice #).
 *
 * The fallback is load-bearing on CASH basis. The cash figures come from the
 * journal PROJECTION, which synthesizes lines from the domain tables and has no
 * journal entry to number — `sourceRef` is null for every row (issue #486). Only
 * `docNumber` is populated there, so without this the column would be entirely
 * blank whenever the Balance Sheet / P&L basis chip is set to Cash.
 *
 * `docNumber` is NOT passed through `shortRef`: document numbers are free-form,
 * so splitting on ':' would truncate a reference like "Zego: 4412".
 */
export const detailNum = (l: Pick<GlReportLine, 'sourceRef' | 'docNumber'>): string =>
	shortRef(l.sourceRef) || (l.docNumber ?? '');

/** Sort key for the Num column — numeric where possible, mirroring `detailNum`. */
export const detailNumSort = (l: Pick<GlReportLine, 'sourceRef' | 'docNumber'>): number =>
	Number(detailNum(l)) || 0;

// Income / liability / equity accounts are credit-normal: their natural balance
// is credits − debits, the opposite of the debit-normal figure the GL report
// returns (debit − credit). The modal flips the sign so these read in their
// natural orientation (income positive), matching the P&L. Assets / expenses are
// debit-normal → no flip. Mirrors DEBIT_NORMAL_TYPES in api/reports/accounting.ts.
const CREDIT_NORMAL_TYPES = new Set(['INC', 'OINC', 'AP', 'OCLIAB', 'LTLIAB', 'EQUITY']);

export const naturalBalanceSign = (accountType: string): 1 | -1 =>
	CREDIT_NORMAL_TYPES.has(accountType) ? -1 : 1;

// Income / expense are P&L (flow) accounts: they measure period activity and
// carry no opening/running balance — only Balance Sheet (stock) accounts do.
// The modal hides the opening + running-balance UI for these and shows the
// period Net instead. Mirrors INCOME_TYPES ∪ EXPENSE_TYPES in
// api/reports/accounting.ts.
const PL_TYPES = new Set(['INC', 'OINC', 'COGS', 'EXP', 'EXEXP', 'OEXP']);

export const isPlAccountType = (accountType: string): boolean => PL_TYPES.has(accountType);

export interface GlDetailLine extends GlReportLine {
	/** Running balance AFTER this posting — computed oldest→newest by the modal. */
	balanceCents: number;
}

export type GlDetailSortKey =
	| 'postedOn' | 'type' | 'num' | 'name' | 'memo' | 'debit' | 'credit' | 'balance';

const sortValue = (l: GlDetailLine, key: GlDetailSortKey): string | number => {
	switch (key) {
		case 'postedOn': return l.postedOn;
		case 'type': return sourceLabel(l.sourceKind); // sort by the LABEL the user sees
		case 'num': return detailNumSort(l);
		case 'name': return (l.counterparty ?? '').toLowerCase();
		case 'memo': return (l.memo ?? '').toLowerCase();
		case 'debit': return l.debitCents;
		case 'credit': return l.creditCents;
		case 'balance': return l.balanceCents;
	}
};

/**
 * Sort a copy of the GL-detail lines by a column. Ties break on posted date then
 * sourceId so the order is deterministic (stable across renders). Note the
 * "balance" column is a running total computed in chronological order, so
 * sorting by any non-date column leaves those balances attached to their own
 * row — meaningful per-row, but no longer a top-to-bottom running total (this
 * matches QuickBooks register behavior).
 */
export function sortGlDetailLines(
	lines: GlDetailLine[],
	key: GlDetailSortKey,
	asc: boolean,
): GlDetailLine[] {
	const out = [...lines];
	out.sort((a, b) => {
		const av = sortValue(a, key);
		const bv = sortValue(b, key);
		let cmp = typeof av === 'number' && typeof bv === 'number'
			? av - bv
			: String(av).localeCompare(String(bv));
		if (cmp === 0) cmp = a.postedOn - b.postedOn;
		if (cmp === 0) cmp = String(a.sourceId).localeCompare(String(b.sourceId));
		return asc ? cmp : -cmp;
	});
	return out;
}
